// useRealTimeNotifs + ActivityToast
// Foreground in-app notifications. Subscribes to the activities table and
// surfaces directed events (race invites, watch-together, sync, nudges) plus
// friend activity as toasts. Toasts spring in, stack with depth, show a
// depleting timer bar, can be swiped up to dismiss, and actionable ones
// persist until acted on. Push covers background; this covers foreground.
import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { X, Flag, Tv, Sparkles, Zap, Film, Trophy, Star, Play, Flame } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { activityLabel } from '../lib/activity'
import { activityDest } from '../lib/activityRoute'
import { Avatar } from './Avatar'
import { isOverlayBusy } from '../lib/overlayBusy'

const TOAST_TYPES = new Set(['race_invite', 'watch_together', 'room_invite', 'insync', 'race_nudge'])
// Persistent = actionable; never auto-dismisses, waits for the user.
const PERSISTENT_TYPES = new Set(['race_invite', 'watch_together', 'room_invite'])

const TYPE_META = {
  race_invite:    { Icon: Flag,     label: 'Race invite',    action: 'View race', color: 'var(--purple)' },
  watch_together: { Icon: Tv,       label: 'Watch together', action: 'Join',      color: 'var(--blue-soft)' },
  room_invite:    { Icon: Tv,       label: 'Watch together', action: 'Join',      color: 'var(--blue-soft)' },
  insync:         { Icon: Sparkles, label: 'In sync',        action: 'View show', color: 'var(--green)' },
  race_nudge:     { Icon: Zap,      label: 'Race alert',     action: 'Catch up',  color: 'var(--amber)' },
  recommended:    { Icon: Film,     label: 'Recommendation', action: 'View',      color: 'var(--rose)' },
}

const FRIEND_TYPES = new Set(['episode', 'completed', 'rated', 'binge', 'watching'])
const FRIEND_META = {
  episode:   { Icon: Play,   color: 'var(--blue-soft)' },
  completed: { Icon: Trophy, color: 'var(--amber)' },
  rated:     { Icon: Star,   color: 'var(--amber)' },
  binge:     { Icon: Flame,  color: 'var(--rose)' },
  watching:  { Icon: Play,   color: 'var(--green)' },
}

export function useRealTimeNotifs(user, following = []) {
  const [toasts, setToasts] = useState([])
  const seenRef  = useRef(new Set())
  const mountRef = useRef(Date.now())
  const followingRef = useRef(following)
  useEffect(() => { followingRef.current = following || [] }, [following])

  // Co-watch pacts involving me → Set of `${otherUserId}:${showId}`. Used to upgrade
  // a friend's "watched S2E5" toast into a "mark yours too" co-watch nudge. Errors
  // (table missing) just leave the set empty → no enrichment, never breaks toasts.
  const cowatchRef = useRef(new Set())
  useEffect(() => {
    if (!user?.id) return
    const loadPacts = () => {
      supabase.from('cowatch').select('user_id, partner_id, show_id')
        .or(`user_id.eq.${user.id},partner_id.eq.${user.id}`)
        .then(({ data, error }) => {
          if (error || !data) return
          const s = new Set()
          data.forEach(r => s.add(`${r.user_id === user.id ? r.partner_id : r.user_id}:${r.show_id}`))
          cowatchRef.current = s
        })
    }
    loadPacts()
    // Refresh when a pact is declared/ended THIS session so the "mark yours too" nudge
    // fires for it without a reload (cowatchRef was previously loaded once on mount).
    window.addEventListener('nextep:cowatch-changed', loadPacts)
    return () => window.removeEventListener('nextep:cowatch-changed', loadPacts)
  }, [user?.id])

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])
  const dismissAll = useCallback(() => setToasts([]), [])

  // Cap the friend filter so the realtime subscription string stays bounded — the
  // ambient-toast feature degrades gracefully past this (those friends' activity
  // still shows in the bell on next load), while directed toasts are never capped.
  const followFilter = (following || []).slice(0, 100).join(',')
  useEffect(() => {
    if (!user?.id) return

    const handleRow = async (row) => {
      if (!row || seenRef.current.has(row.id)) return
      const rowTime = new Date(row.created_at).getTime()
      if (rowTime < mountRef.current - 5000) return
      // A co-watch invite arrives as a directed 'notice' carrying data.room_id (the
      // durable bell entry the watch-together function writes). TOAST_TYPES doesn't
      // include 'notice', so the invite NEVER toasted — the recipient only found it by
      // hunting in the bell (Jason 2026-07-26). Treat notice+room_id as a first-class
      // actionable invite toast; act() already joins via data.room_id.
      const isRoomInviteNotice = row.type === 'notice' && !!row.data?.room_id
      const isDirected = row.user_id === user.id && (TOAST_TYPES.has(row.type) || isRoomInviteNotice)
      const isFriend   = row.user_id !== user.id
        && FRIEND_TYPES.has(row.type)
        && row.data?.public === true
        && followingRef.current.includes(row.user_id)
      if (!isDirected && !isFriend) return
      seenRef.current.add(row.id)
      let actorName = null, actorUsername = null
      if (isFriend) {
        try {
          const { data: p } = await supabase.from('profiles').select('display_name, username').eq('id', row.user_id).single()
          actorName = p?.display_name || p?.username || 'A friend'
          actorUsername = p?.username || null
        } catch { actorName = 'A friend' }
      }
      const toast = {
        id: row.id, type: row.type, show_id: row.show_id, show_name: row.show_name,
        data: row.data || {}, created_at: row.created_at,
        // actorUsername/actorId let the avatar tap through to the person's profile —
        // the card's own tap goes to the SHOW, so the person needed a separate route.
        friend: isFriend, actorName, actorUsername, actorId: isFriend ? row.user_id : null,
        // A room-invite notice is actionable (Join) — keep it on screen like the other
        // invite types instead of auto-dismissing while the recipient reaches for it.
        persistent: PERSISTENT_TYPES.has(row.type) || isRoomInviteNotice,
        // A pact-mate just watched this episode → upgrade to a "mark yours too" nudge.
        cowatch: isFriend && row.type === 'episode' && cowatchRef.current.has(`${row.user_id}:${row.show_id}`),
      }
      // One card per (friend, show) for episode marks (2026-09-14): a friend marking a
      // run of episodes a few seconds apart used to land one card PER episode, stacked
      // up to four high — a third of the screen for one person's one sitting. The
      // newest replaces the older card for the same friend + show in place, so the
      // stack reads "Melissa watched S11·E7" once, not E4/E5/E6/E7 four times. Same
      // collapse the push side now does with a per-(actor, show) notification tag.
      setToasts(prev => {
        if (prev.find(t => t.id === toast.id)) return prev
        const dup = toast.type === 'episode' && toast.actorId
          ? prev.findIndex(t => t.type === 'episode' && t.actorId === toast.actorId && t.show_id === toast.show_id)
          : -1
        const rest = dup >= 0 ? prev.filter((_, i) => i !== dup) : prev
        return [toast, ...rest].slice(0, 4)
      })
    }

    // Server-side filters keep this per-user instead of receiving EVERY platform-wide
    // activity INSERT. The old unfiltered subscription fanned out one insert to every
    // logged-in client (~O(users²) during a surge) and discarded most in JS. Two
    // filtered streams on one channel: rows addressed TO me (directed toasts), and rows
    // authored by people I follow (ambient friend toasts). followingRef still gates the
    // handler so a filter-cap overflow can never surface a non-followed row.
    const channel = supabase.channel('notifs-' + user.id)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'activities', filter: `user_id=eq.${user.id}` },
        (payload) => handleRow(payload.new))
    if (followFilter) {
      channel.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'activities', filter: `user_id=in.(${followFilter})` },
        (payload) => handleRow(payload.new))
    }
    channel.subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [user?.id, followFilter])

  // Local bridge: any code can fire the same stylish toast without a DB round-trip
  //   window.dispatchEvent(new CustomEvent('nextep:toast', {
  //     detail: { title, body, show_id, accent, type, actionLabel, persistent } }))
  useEffect(() => {
    const onToast = (e) => {
      const d = e.detail || {}
      const toast = {
        id: 'local-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7),
        type: d.type || 'insync', show_id: d.show_id, show_name: d.show_name,
        data: d.data || {}, created_at: new Date().toISOString(),
        friend: false, actorName: null, persistent: !!d.persistent,
        custom: true, title: d.title, body: d.body, accent: d.accent, actionLabel: d.actionLabel,
        key: d.key || null,
      }
      // `detail.key` (2026-09-14): a follow-up toast about the SAME thing replaces the
      // earlier card instead of stacking under it — the Pulse sheet's "🎬 Rating
      // saved" then "😍 Reaction saved" for one episode were two cards for one action.
      setToasts(prev => {
        const rest = toast.key ? prev.filter(t => t.key !== toast.key) : prev
        return [toast, ...rest].slice(0, 4)
      })
    }
    window.addEventListener('nextep:toast', onToast)
    return () => window.removeEventListener('nextep:toast', onToast)
  }, [])

  return { toasts, dismiss, dismissAll }
}

function SingleToast({ toast, onDismiss, depth }) {
  const navigate = useNavigate()
  const [leaving, setLeaving]   = useState(false)
  const [drag, setDrag]         = useState(0)
  const [dragging, setDragging] = useState(false)
  const [paused, setPaused]     = useState(false)
  const startY = useRef(null)
  const DURATION = 6800

  const close = useCallback((flick) => {
    setLeaving(prev => {
      if (prev) return prev
      setTimeout(onDismiss, 300)
      return true
    })
    if (flick) setDrag(-170)
  }, [onDismiss])

  useEffect(() => {
    if (toast.persistent || paused || leaving || dragging) return
    const t = setTimeout(() => close(true), DURATION)
    return () => clearTimeout(t)
  }, [toast.persistent, paused, leaving, dragging, close])

  const showName = toast.show_name
  const fromName = toast.data?.from_name || toast.data?.sync_name || 'Someone'
  let meta, headerLabel, bodyText, isFriend
  if (toast.friend) {
    const fm = FRIEND_META[toast.type] || { Icon: Sparkles, color: 'var(--purple)' }
    meta = { Icon: fm.Icon, color: fm.color, action: 'View show' }
    headerLabel = toast.actorName || 'Friend activity'
    bodyText = activityLabel(toast.type, showName, toast.data || {})
    isFriend = true
    if (toast.cowatch) {
      meta = { Icon: Tv, color: 'var(--amber)', action: 'Mark yours' }
      headerLabel = '🍿 Watching together'
      bodyText = `${toast.actorName || 'They'} just watched S${toast.data?.season}·E${toast.data?.episode} of ${showName}, mark yours too?`
    }
  } else {
    meta = TYPE_META[toast.type] || { Icon: Sparkles, label: 'Notification', action: 'View', color: 'var(--purple)' }
    headerLabel = meta.label
    bodyText = ({
      race_invite:    fromName + ' challenged you to a race' + (showName ? ' on ' + showName : ''),
      watch_together: fromName + ' invited you to watch together' + (showName ? ' on ' + showName : ''),
      room_invite:    fromName + ' invited you to watch together' + (showName ? ' on ' + showName : ''),
      insync:         'You and ' + fromName + ' are on the same episode of ' + (showName || 'a show'),
      race_nudge:     fromName + ' is pulling ahead on ' + (showName || 'your race') + ' - catch up!',
      recommended:    fromName + ' recommends ' + (showName || 'a show'),
    })[toast.type] || ('New activity' + (showName ? ' on ' + showName : ''))
    isFriend = false
    // Co-watch invite delivered as a 'notice' (see handleRow): give it the full
    // Watch-Together treatment — Tv icon, Join action, and the server's own message
    // ("Jason invited you to watch The Bear together") as the body.
    if (toast.type === 'notice' && toast.data?.room_id) {
      meta = { Icon: Tv, label: 'Watch together', action: 'Join', color: 'var(--blue-soft)' }
      headerLabel = meta.label
      if (toast.data.message) bodyText = toast.data.message
    }
  }
  // Custom (locally-dispatched) toasts override the derived label/body/accent.
  if (toast.custom) {
    if (toast.title) headerLabel = toast.title
    if (toast.body)  bodyText = toast.body
    if (toast.actionLabel) meta = { ...meta, action: toast.actionLabel }
  }
  const accent = toast.accent || meta.color
  const Icon = meta.Icon

  const act = () => {
    // Co-watch "mark yours too": don't dump the user on the show page to re-find the
    // episode — mark the exact episode the pact-mate just watched, inline. App owns wl
    // (and the show's season data), so fire the event and let it advance my progress +
    // confirm. This is the one-tap sync that "counts for both" implies. Everything else
    // keeps the plain navigate-to-show behavior.
    if (toast.cowatch && toast.show_id && toast.data?.season != null && toast.data?.episode != null) {
      window.dispatchEvent(new CustomEvent('nextep:cowatch-mark', { detail: {
        show_id: toast.show_id, season: toast.data.season, episode: toast.data.episode,
        show_name: showName, actorName: toast.actorName,
      } }))
      close(false)
      return
    }
    // Watch-Together room invite: JOIN the room, not just navigate. Stash the roomId so
    // ShowDetail joins on arrival (it owns the room hook) → the Viewing Room lobby mounts.
    // Gate on the room_id itself, not the toast type: the invite arrives as a 'notice'
    // (the durable bell entry), so keying off type === 'room_invite' meant a real invite
    // with a room_id never took the join path and the recipient spawned their own room.
    if (toast.data?.room_id && toast.show_id) {
      try { sessionStorage.setItem('nextep_join_room', String(toast.data.room_id)) } catch {}
      close(false)
      navigate('/show/' + toast.show_id)
      return
    }
    close(false)
    // Custom toasts can point anywhere (e.g. a post-follow CTA → the person's profile).
    if (toast.data?.route) { navigate(toast.data.route); return }
    // Otherwise route to the SPECIFIC content (review/aftershow/episode), not a generic
    // show page — same dead-end fix as the notification inbox (audit 2026-08-11).
    const d = activityDest(toast.type, toast.show_id)
    if (d) navigate(d)
  }

  const down = (e) => { startY.current = e.clientY }
  const move = (e) => {
    if (startY.current == null) return
    if (!dragging) setDragging(true)
    const dy = e.clientY - startY.current
    setDrag(dy < 0 ? dy : dy * 0.22)
  }
  const up = () => {
    if (startY.current == null) return
    const released = drag
    startY.current = null
    setDragging(false)
    if (released < -48) close(true)
    else setDrag(0)
  }

  const dragFade = Math.min(Math.abs(Math.min(drag, 0)) / 170, 0.85)
  return (
    <div
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        position: 'relative', overflow: 'hidden',
        background: `linear-gradient(135deg, color-mix(in srgb, ${accent} 14%, var(--bg-raised)), color-mix(in srgb, ${accent} 6%, var(--bg)))`,
        border: '1px solid ' + accent + '40',
        borderLeft: '3px solid ' + accent,
        borderRadius:'var(--radius-lg)',
        padding: '13px 14px',
        display: 'flex', alignItems: 'flex-start', gap: 11,
        boxShadow: '0 10px 36px rgba(0,0,0,0.55), 0 0 26px ' + accent + (toast.persistent ? '30' : '14'),
        backdropFilter: 'blur(14px)',
        fontFamily: 'var(--font)',
        transform: 'translateY(' + drag + 'px) scale(' + (1 - depth * 0.025) + ')',
        opacity: leaving ? 0 : (1 - depth * 0.06 - dragFade),
        transition: dragging ? 'none' : 'transform 0.32s cubic-bezier(0.34,1.3,0.64,1), opacity 0.3s ease',
        '--nxa': (!leaving && depth === 0 && drag === 0) ? 'atIn 0.45s cubic-bezier(0.34,1.4,0.64,1)' : 'none',
        cursor: 'grab', touchAction: 'pan-x', WebkitUserSelect: 'none', userSelect: 'none',
      }}
    >
      {isFriend
        ? (toast.actorUsername
            ? <div onClick={(e) => { e.stopPropagation(); navigate(`/u/${toast.actorUsername}`) }}
                role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); navigate(`/u/${toast.actorUsername}`) } }}
                aria-label={`Open ${toast.actorName}'s profile`}
                style={{ flexShrink: 0, cursor: 'pointer', borderRadius: 'var(--radius-full)' }}>
                <Avatar name={toast.actorName} size={38} />
              </div>
            : <Avatar name={toast.actorName} size={38} />)
        : <div style={{
            width: 38, height: 38, borderRadius:'var(--radius)', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: accent + '1f', border: '1px solid ' + accent + '40',
            boxShadow: toast.persistent ? '0 0 14px ' + accent + '55' : 'none',
          }}><Icon size={19} color={accent} strokeWidth={2.2}/></div>}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{ color: accent, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 'var(--toast-eyebrow-tracking)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{headerLabel}</span>
          {toast.persistent && <span style={{ fontSize: 8, fontWeight: 700, color: accent, background: accent + '22', borderRadius: 5, padding: '1px 5px', letterSpacing: '0.06em', flexShrink: 0 }}>ACTION</span>}
        </div>
        <div style={{ color: 'var(--text-strong)', fontSize: 13, lineHeight: 1.4, marginBottom: toast.show_id ? 9 : 0 }}>{bodyText}</div>
        {toast.show_id && (
          <button onClick={act} style={{
            background: accent + '20', border: '1px solid ' + accent + '45',
            borderRadius: 9, padding: '6px 13px', cursor: 'pointer',
            color: accent, fontWeight: 700, fontSize: 12, fontFamily: 'var(--font)',
          }}>{meta.action}</button>
        )}
      </div>

      <button onClick={() => close(true)} aria-label='Dismiss' style={{
        background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 0, margin: '-8px -8px 0 0', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, lineHeight: 1,
      }}><X size={14}/></button>

      {!toast.persistent && (
        <div style={{
          position: 'absolute', left: 0, bottom: 0, height: 3, width: '100%',
          transformOrigin: 'left', background: accent, opacity: 0.55,
          '--nxa': 'toastBar ' + DURATION + 'ms linear forwards',
          animationPlayState: (paused || dragging || leaving) ? 'paused' : 'running',
        }}/>
      )}
    </div>
  )
}

export function ActivityToastStack({ toasts, onDismiss }) {
  // Defer the top rail while a full-screen sheet/cinematic owns the screen — the SAME hold
  // the bottom Celebration honors (What's New update sheet = __nextepModalHold, a card/finale
  // cinematic = __nextepFinaleActive, the celebration stage = __nextepWrapUntil). Toasts stay
  // QUEUED (still in state, not dropped) and present the moment it clears — no more invite
  // toast stacking on the update sheet (Jason 2026-07-22). Re-check on a poll + close events.
  const [, bump] = useState(0)
  const held = !!toasts?.length && isOverlayBusy()
  useEffect(() => {
    if (!held) return
    const iv = setInterval(() => bump(n => n + 1), 400)
    const onClear = () => bump(n => n + 1)
    window.addEventListener('nextep:wrap-done', onClear)
    window.addEventListener('nextep:card-done', onClear)
    return () => { clearInterval(iv); window.removeEventListener('nextep:wrap-done', onClear); window.removeEventListener('nextep:card-done', onClear) }
  }, [held])
  if (!toasts?.length || held) return null
  return createPortal(
    <div style={{
      position: 'fixed', top: 'max(14px, env(safe-area-inset-top))',
      left: 12, right: 12, zIndex: 'var(--z-celebration)',
      display: 'flex', flexDirection: 'column', gap: 9,
      pointerEvents: 'none', maxWidth: 520, marginLeft: 'auto', marginRight: 'auto',
    }}>
      {toasts.map((toast, i) => (
        <div key={toast.id} style={{ pointerEvents: 'auto' }}>
          <SingleToast toast={toast} depth={i} onDismiss={() => onDismiss(toast.id)}/>
        </div>
      ))}
      <style>{'@keyframes atIn{0%{opacity:0;transform:translateY(-26px) scale(0.9);filter:blur(3px)}55%{filter:blur(0)}100%{opacity:1;transform:translateY(0) scale(1);filter:blur(0)}} '}</style>
    </div>,
    document.body
  )
}
